import API from './API';
import Utility from './Utility';
import { config } from "./Config";

// Session tokens are carried by the HttpOnly cookie set by the server, never stored here.
// The authentication state of a tab lives in sessionStorage, so opening a link in a tab never changes
// the session type of another tab. The only shared flag tells a new tab that a password session is open.
const SHARED_PASSWORD_SESSION_KEY = 'passwordSessionOpen';

class Auth {
  login(lang, authViaPassword, dataAuthViaURL, callback) {
    sessionStorage.setItem('auth', 'true');
    sessionStorage.setItem('authViaPassword', authViaPassword === true ? 'true' : 'false');
    sessionStorage.setItem('authViaURL', JSON.stringify({
      codePage : dataAuthViaURL.codePage,
      id : dataAuthViaURL.id
    }));
    if (authViaPassword === true) {
      localStorage.setItem(SHARED_PASSWORD_SESSION_KEY, 'true');
    }
    this.changeLangue(lang);
    callback();
  }

  /**
   * Opens the session of the current tab when it has none: a password session already open in another tab
   * is reused through the cookie, otherwise the link of the page opens a link session.
   */
  openTabSession(lang: string, codePage: string | null, id: string | null): void {
    if (this.isAuthenticated()) {
      return;
    }
    if (this.hasSharedPasswordSession()) {
      this.login(lang, true, { codePage: null, id: null }, () => {});
      return;
    }
    this.login(lang, false, { codePage: codePage, id: id }, () => {});
  }

  /**
   * Replaces the session of the current tab by the link session of the page, other tabs are untouched.
   */
  openLinkSession(lang: string, codePage: string | null, id: string | null): void {
    this.clear();
    this.login(lang, false, { codePage: codePage, id: id }, () => {});
  }

  logout() {
    let url = Utility.getUrlAjax()+'/signOutAction';
    const lang = localStorage.getItem('lang');
    const token = Utility.getRouteToken();
    this.endTabSession();
    API
      .get(url)
      .then(res => {
        let passer = false;
        if(res !== undefined && res !== null) {
          if(res.data !== undefined && res.data !== null) {
            if(res.data.data !== undefined && res.data.data !== null) {
              if(res.data.data.token !== undefined && res.data.data.token !== null) {
                document.location.href = `${config.url.URL}${res.data.data.token}/${lang}/login`;
                passer = true;
              }
            }
          }
        }

        if(passer === false) {
          document.location.href = `${config.url.URL}${token}/${lang}/login`;
        }
      })
      .catch(err => {
        console.error(err)
        document.location.href = `${config.url.URL}${token}/${lang}/login`;
      });
  }

  // Resets the authentication state of the current tab only.
  clear(): void {
    sessionStorage.removeItem('auth');
    sessionStorage.removeItem('authViaPassword');
    sessionStorage.removeItem('authViaURL');
    sessionStorage.removeItem('panierState');
  }

  /**
   * Ends the session of the current tab on sign out. Only a password tab ends the password session shared
   * by all tabs: a link tab signing out keeps the password session of the other tabs. The language is not a
   * secret and stays shared, so the tabs still open keep building valid AJAX paths.
   */
  endTabSession(): void {
    if (this.isAuthenticatedViaPassword()) {
      this.clearSharedSession();
    }
    this.clear();
  }

  // Forgets the password session shared by all tabs.
  clearSharedSession(): void {
    localStorage.removeItem(SHARED_PASSWORD_SESSION_KEY);
  }

  /**
   * Resets the current tab before a login. A password tab sent back to the login page means the cookie
   * session is over: new tabs must no longer reuse it. A link tab leaves the shared password session untouched.
   */
  resetTabForLogin(): void {
    if (this.isAuthenticatedViaPassword()) {
      this.clearSharedSession();
    }
    this.clear();
  }

  // Removes session tokens and the shared authentication state persisted by previous versions.
  purgeLegacySessionKeys(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenSession');
    localStorage.removeItem('auth');
    localStorage.removeItem('authViaPassword');
    localStorage.removeItem('authViaURL');
  }

  creation(callback) {
    callback();
  }

  isAuthenticated(): boolean {
    return sessionStorage.getItem('auth') === 'true';
  }

  isAuthenticatedViaPassword(): boolean {
    return sessionStorage.getItem('authViaPassword') === 'true';
  }

  hasSharedPasswordSession(): boolean {
    return localStorage.getItem(SHARED_PASSWORD_SESSION_KEY) === 'true';
  }

  getAuthViaURL() {
    let authViaURL  = JSON.parse(sessionStorage.getItem('authViaURL')),
        id          = null,
        codePage    = null;

    if(authViaURL !== undefined && authViaURL !== null && authViaURL !== '') {
      if(authViaURL.id !== undefined && authViaURL.id !== null) {
        id = authViaURL.id;
      }
      if(authViaURL.codePage !== undefined && authViaURL.codePage !== null) {
        codePage = authViaURL.codePage;
      }
    }

    return {
      codePage  : codePage,
      id        : id
    }
  }

  changeLangue(lang) {
    localStorage.setItem('lang', lang);
    let formatDate = 'DD MMMM YYYY';
    if(lang === 'EN') {
      formatDate = 'DD MMMM YYYY';
    }
    localStorage.setItem('formatDate', formatDate);
  }
}

export default new Auth();
